import { motion } from 'framer-motion';
import { Target, Eye, Heart, Users, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const About = () => {
  const values = [
    {
      icon: <Target className="w-10 h-10" />,
      title: 'Excellence',
      description: 'We strive for excellence in every service we provide, ensuring the highest quality outcomes for our clients.',
      color: '#3498DB'
    },
    {
      icon: <Eye className="w-10 h-10" />,
      title: 'Transparency',
      description: 'Clear communication and transparent processes are the foundation of our client relationships.',
      color: '#27AE60'
    },
    {
      icon: <Heart className="w-10 h-10" />,
      title: 'Client-Centric',
      description: 'Your success is our success. We tailor every solution to meet your unique needs and goals.',
      color: '#5DADE2'
    },
    {
      icon: <Users className="w-10 h-10" />,
      title: 'Expertise',
      description: 'Our team brings years of experience and deep knowledge of UAE business landscape.',
      color: '#D4AF37'
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.25, 0.1, 0.25, 1] } }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="about-page">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center text-white pt-20" data-testid="about-hero-section">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=1920" 
            alt="Dubai"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#0a0a0a]/70 to-[#0a0a0a]" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <motion.div 
              className="flex items-center justify-center gap-4 mb-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <div className="w-20 h-[1px] bg-gradient-to-r from-transparent to-[#3498DB]" />
              <span className="text-[#3498DB] font-semibold tracking-[0.2em] uppercase text-sm">About Us</span>
              <div className="w-20 h-[1px] bg-gradient-to-l from-transparent to-[#3498DB]" />
            </motion.div>
            
            <h1 className="text-5xl md:text-7xl font-bold mb-6" data-testid="about-hero-title">
              About <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#3498DB] to-[#27AE60]">SAM Relocation</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Your partner in navigating the UAE business landscape with confidence
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-24 bg-[#0a0a0a]" data-testid="our-story-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1518684079-3c830dcef090?w=800"
                  alt="Dubai Skyline"
                  className="w-full h-[500px] object-cover"
                  data-testid="about-story-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] to-transparent opacity-60" />
              </div>
              <motion.div
                className="absolute -bottom-8 -right-8 bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
              >
                <div className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#3498DB] to-[#27AE60]">15+</div>
                <div className="text-gray-400 text-sm">Years of Excellence</div>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-[2px] bg-gradient-to-r from-[#27AE60] to-transparent" />
                <span className="text-[#27AE60] font-semibold tracking-[0.2em] uppercase text-sm">Our Story</span>
              </div>
              
              <h2 className="text-4xl md:text-5xl font-bold mb-8 text-white" data-testid="our-story-heading">
                Building Success in the <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#3498DB] to-[#27AE60]">UAE</span>
              </h2>
              
              <div className="space-y-6 text-lg text-gray-400 leading-relaxed">
                <p>
                  SAM Relocation Group is an international consulting firm based in Dubai, UAE, specializing in comprehensive business solutions for entrepreneurs, investors, companies, and expatriates.
                </p>
                <p>
                  With deep expertise in business setup, relocation services, corporate administration, and financial consulting, we offer a 360° approach to ensure your success in the UAE market.
                </p>
                <p>
                  Our team of seasoned professionals combines local knowledge with international experience, providing tailored solutions that address the unique challenges of doing business in Dubai.
                </p>
              </div>
              
              <Link 
                to="/contact"
                className="inline-flex items-center gap-3 mt-8 px-8 py-4 bg-gradient-to-r from-[#3498DB] to-[#27AE60] text-white font-semibold rounded-full hover:shadow-[0_0_30px_rgba(52,152,219,0.4)] transition-all duration-300"
              >
                Get in Touch
                <ArrowRight size={20} />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 bg-gradient-to-b from-[#0a0a0a] to-[#111]" data-testid="mission-vision-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-white/5 to-transparent border border-white/10 p-10"
              data-testid="mission-card"
            >
              <div className="absolute top-0 right-0 w-40 h-40 bg-[#3498DB]/10 rounded-full blur-3xl" />
              <div className="relative">
                <div className="w-16 h-16 bg-[#3498DB]/10 rounded-2xl flex items-center justify-center mb-6">
                  <Target className="w-8 h-8 text-[#3498DB]" />
                </div>
                <h3 className="text-3xl font-bold mb-4 text-white">Our Mission</h3>
                <div className="w-16 h-1 bg-gradient-to-r from-[#3498DB] to-transparent mb-6" />
                <p className="text-lg text-gray-400 leading-relaxed">
                  To empower individuals and businesses to realize their ambitions in the UAE by providing expert guidance, comprehensive support, and innovative solutions throughout their journey.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-white/5 to-transparent border border-white/10 p-10"
              data-testid="vision-card"
            >
              <div className="absolute top-0 right-0 w-40 h-40 bg-[#27AE60]/10 rounded-full blur-3xl" />
              <div className="relative">
                <div className="w-16 h-16 bg-[#27AE60]/10 rounded-2xl flex items-center justify-center mb-6">
                  <Eye className="w-8 h-8 text-[#27AE60]" />
                </div>
                <h3 className="text-3xl font-bold mb-4 text-white">Our Vision</h3>
                <div className="w-16 h-1 bg-gradient-to-r from-[#27AE60] to-transparent mb-6" />
                <p className="text-lg text-gray-400 leading-relaxed">
                  To be the most trusted and preferred partner for international business establishment and relocation in the UAE, recognized for our excellence, integrity, and commitment to client success.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-24 bg-[#0a0a0a]" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="w-20 h-[1px] bg-gradient-to-r from-transparent to-[#5DADE2]" />
              <span className="text-[#5DADE2] font-semibold tracking-[0.2em] uppercase text-sm">Our Values</span>
              <div className="w-20 h-[1px] bg-gradient-to-l from-transparent to-[#5DADE2]" />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white" data-testid="values-heading">
              Core <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#5DADE2] to-[#27AE60]">Values</span>
            </h2>
          </motion.div>

          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {values.map((value, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="group relative overflow-hidden rounded-3xl bg-gradient-to-br from-white/5 to-transparent border border-white/10 p-8 text-center hover:border-white/20 transition-all duration-500"
                data-testid={`value-card-${index}`}
              >
                <motion.div 
                  className="w-20 h-20 mx-auto rounded-2xl flex items-center justify-center mb-6 transition-all duration-500 group-hover:scale-110"
                  style={{ background: `${value.color}15`, color: value.color }}
                >
                  {value.icon}
                </motion.div>
                <h3 className="text-xl font-bold mb-3 text-white">{value.title}</h3>
                <p className="text-gray-400 leading-relaxed">{value.description}</p>
                
                <motion.div
                  className="absolute bottom-0 left-0 h-1 bg-gradient-to-r"
                  style={{ background: `linear-gradient(90deg, ${value.color}, transparent)` }}
                  initial={{ width: 0 }}
                  whileHover={{ width: '100%' }}
                  transition={{ duration: 0.5 }}
                />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1518684079-3c830dcef090?w=1920" 
            alt="Dubai Night"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#0a0a0a]/85" />
        </div>
        
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Start Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#3498DB] to-[#27AE60]">Journey</span>?
            </h2>
            <p className="text-xl text-gray-300 mb-10">
              Let us help you achieve your goals in the UAE
            </p>
            <Link 
              to="/contact"
              className="inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-[#3498DB] to-[#27AE60] text-white font-bold text-lg rounded-full hover:shadow-[0_0_40px_rgba(52,152,219,0.5)] transition-all duration-300"
            >
              Contact Us Today
              <ArrowRight size={22} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
